/**
 * How a company's request for more (D2) and the data it asks for are NAMED, on
 * every citizen surface — the web card and dialog, the phone sheet.
 *
 * Every scope and every test maps to its LITERAL catalogue key, so the
 * key-usage gate checks each one and the mapped type fails the build when the
 * consent vocabulary in @tonnie/foundation gains a member without a name here.
 * A scope or test this build does not know (a newer server) renders its RAW
 * value, loudly — a missing name is visible, never papered over.
 */
import { isConsentScope, type ConsentScope } from '@tonnie/foundation';

type Translate = (key: string, options?: Record<string, unknown>) => string;

const SCOPE_LABEL: { readonly [S in ConsentScope]: (t: Translate) => string } = {
  checkup: (t) => t('journey.consent.scope.checkup'),
  teleportal: (t) => t('journey.consent.scope.teleportal'),
  qultureup: (t) => t('journey.consent.scope.qultureup'),
  care: (t) => t('journey.consent.scope.care'),
  irr: (t) => t('journey.consent.scope.irr'),
  testit: (t) => t('journey.consent.scope.testit'),
  passport_stamps: (t) => t('journey.consent.scope.passport_stamps'),
  pitch_video: (t) => t('journey.consent.scope.pitch_video'),
  cv: (t) => t('journey.consent.scope.cv'),
  household: (t) => t('journey.consent.scope.household'),
  care_checkins: (t) => t('journey.consent.scope.care_checkins'),
};

/** A consent scope's name — "My CheckUp results". */
export function consentScopeLabel(scope: string, t: Translate): string {
  return isConsentScope(scope) ? SCOPE_LABEL[scope](t) : scope;
}

/** The tests a company can ask for (the B2B catalog), by id. */
const TEST_LABEL: Readonly<Record<string, (t: Translate) => string>> = {
  pitch: (t) => t('testit.test.pitch'),
  'language.en': (t) => t('testit.test.languageEn'),
  'language.pt': (t) => t('testit.test.languagePt'),
  motivational: (t) => t('testit.test.motivational'),
};

/** A test's name — "English". */
export function testitTestLabel(testId: string, t: Translate): string {
  const label = Object.prototype.hasOwnProperty.call(TEST_LABEL, testId)
    ? TEST_LABEL[testId]
    : undefined;
  return label ? label(t) : testId;
}

/** What a request asks for, as the citizen's title says it. */
export function shareRequestTitle(
  t: Translate,
  request: { asks: 'checkup' | 'tests' },
  company: string,
  /** The asked tests' names, already joined in the reader's language. */
  testNames: string
): string {
  return request.asks === 'checkup'
    ? t('invitations.request.titleCheckup', { company })
    : t('invitations.request.titleTests', { company, tests: testNames });
}
